import { GiB, kvBytesPerSequence, type Inputs, type KvGroup } from "./core.ts";
import { GPUS, isApple, type Gpu } from "./hardware.ts";
import { embeddingsInRam, engineBits, isGgufBits, loadedParams, lookupBytes, vocabParams, weightBytes } from "./memory.ts";
import { gpuFamily, reserveFor, type GpuFamily } from "./reserve.ts";
import type { ModelPreset } from "./models.ts";

// ── Vitesse de génération et coût par token ─────────────────────────────────
// Modèle volontairement simple, à présenter comme une estimation (±30 à 50 %) :
// la génération (decode) est limitée par la mémoire. À chaque pas, chaque GPU relit sa
// part des poids actifs et du cache KV de toutes les requêtes en cours. S'y ajoutent un
// coût fixe par couche (noyaux, routage MoE) et les échanges entre GPU. Le calcul
// (TFLOPS) ne limite qu'aux très gros lots. Prefill et décodage spéculatif non modélisés.

// ponytail: constantes calibrées sur des ordres de grandeur publics (vLLM sur H100 :
// Llama 8B ≈ 140 tokens/s, 70B FP8 en TP2 ≈ 60 tokens/s en requête unique) ; à
// recaler si des mesures réelles s'en écartent. llama.cpp : calé GPU par GPU, voir plus bas.
/**
 * vLLM, SGLang, TensorRT-LLM : temps fixe par couche et par token généré, en plus de la lecture des poids
 * (lancement des noyaux, synchronisation entre GPU). Calé sur 133 vitesses mesurées par InferenceX (octobre
 * 2026 : Llama 3.3 70B dense de 1 à 8 GPU, MoE de gpt-oss à DeepSeek) : médiane estimée / mesurée ramenée à 1,00
 * dans chaque famille, contre 1,20 à 1,52 pour les denses avec l'ancien coût (20 µs par couche, 8 µs par
 * synchronisation). tp : par doublement du nombre de GPU reliés en NVLink ou xGMI. Cartes non mesurées
 * (particulier, station de travail) : les valeurs de Hopper.
 */
// AMD Instinct en deux générations (audit du 15 octobre 2026, 51 vitesses InferenceX sur MI300X, MI325X et MI355X) :
// les MoE coûtent 236 µs par couche sur CDNA3 (MI300X, MI325X : pas de FP4 matériel, MXFP4 décompressé par Triton)
// et 112 sur CDNA4 (MI355X : MXFP4 natif d'AITER). Une seule famille à 134 µs donnait gpt-oss-120b 62 à 107 % trop
// rapide sur MI325X et 32 à 43 % trop lent sur MI355X. Écart médian AMD : 19,9 % → 16,2 % sur les 51 mesures
// (23,8 % → 10,5 % sur les modèles de la liste), sans biais (−1,6 %) ; une grille donnait 13,5 % mais 10 % trop
// rapide en moyenne, écartée. Denses : 69 µs, juste sur les deux (Llama 3.3 70B FP8 de 1 à 8 GPU : −8 à +14 %).
const LAYER_US: Record<GpuFamily | "cdna4", { dense: number; moe: number; tp: number }> = {
  other: { dense: 47, moe: 91, tp: 6.8 },
  blackwell: { dense: 47, moe: 64, tp: 11.7 },
  instinct: { dense: 69, moe: 236, tp: 5.2 },
  cdna4: { dense: 69, moe: 112, tp: 5.2 },
};
/** Famille des constantes de vitesse : comme reserve.ts, le MI355X (CDNA4) à part des MI300X et MI325X (CDNA3). */
const speedFamily = (g: Pick<Gpu, "id">): GpuFamily | "cdna4" => (g.id === "mi355x" ? "cdna4" : gpuFamily(g));
/** llama.cpp sans mesure de ce GPU : coût par couche d'un modèle dense. */
const LLAMA_LAYER_FALLBACK_S = 20e-6;

// ── llama.cpp : vitesses calées sur ses tableaux officiels ──────────────────
// Chaque GPU a sa mesure llama-bench (Gpu.bench : Llama 2 7B Q4_0, prompt de 512 tokens et 128 tokens
// générés), ou celle du GPU mesuré le plus proche (benchLike), ramenée à sa puissance de calcul.
//  - Génération : bande passante à 85 % (cartes à mémoire lente : 80 à 85 % mesurés), plus un coût fixe
//    par couche propre au GPU (lancement des noyaux), ce qui reste du temps mesuré par token.
//  - Lecture du prompt : le débit de calcul que la mesure implique (pp512 × FLOP par token).
// Validation (13 octobre 2026) sur le guide gpt-oss de llama.cpp (discussion 15396 : 11 GPU et Mac,
// prompts de 2K à 32K) et des séries en contexte long : génération à 9 % près en médiane (21 % au
// pire), lecture du prompt à 9 % (dense) et 16 % (MoE) près en médiane. Avant ce calage : facteur 2
// sur les GeForce, 3 à 4 sur A100 et H100.
const BENCH_MODEL = { params: 6.74, layers: 32, hidden: 4096, bytes: 3.56 * GiB, prompt: 512 };
/** Lecture du prompt : l'attention (flash attention) tourne moins vite que les produits de matrices. */
const LLAMACPP_ATTENTION = 1.5;
/** MoE, génération : relire des experts épars ne tire qu'une partie de la bande passante. */
const LLAMACPP_MOE_BW = 0.55;
/** Formats « K » (Q6_K, Q5_K_M, Q4_K_M, Q3_K_M), génération : plus coûteux à décoder que le Q4_0 des
 *  mesures. Recoupé sur Llama 3 8B et 70B Q4_K_M, 1 et 2 cartes (XiongjieDai/GPU-Benchmarks-on-LLM-
 *  Inference, mai 2024 : 4090 128 tokens/s, 2 × 4090 19, 2 × 3090 16 ; 2 × RX 7900 XTX ROCm 13). */
const LLAMACPP_KQUANT_BW = 0.9;
/** Rendement de la bande passante en génération avec llama.cpp, selon le modèle et le format. */
const llamaBw = (m: ModelPreset, bits: number, gpu: Gpu) =>
  (moeSplit(m) ? gpu.moeBw ?? LLAMACPP_MOE_BW : 1) * (isGgufBits(bits) && bits !== 8.5 ? LLAMACPP_KQUANT_BW : 1);
/** Lots du prompt traités à la chaîne par llama.cpp (-b 2048, -ub 512 : 4 lots par appel au plus). */
const LLAMACPP_UBATCH_TOKENS = 512, LLAMACPP_UBATCHES = 4;
const benchFlops = 2 * BENCH_MODEL.params * 1e9 + LLAMACPP_ATTENTION * BENCH_MODEL.layers * 4 * BENCH_MODEL.hidden * BENCH_MODEL.prompt / 2;

/** Mesure llama-bench du GPU, ou du GPU proche qui en tient lieu (son débit de prompt ramené au calcul de celui-ci). */
export function llamaBench(gpu: Gpu): { pp: number; tg: number; measured: Gpu } | null {
  if (gpu.bench) return { ...gpu.bench, measured: gpu };
  const like = GPUS.find((g) => g.id === gpu.benchLike);
  return like?.bench ? { pp: like.bench.pp * (gpu.tflops / like.tflops), tg: like.bench.tg, measured: like } : null;
}
/** llama.cpp : coût fixe par couche et par token généré, ce que la mesure tg128 laisse après la lecture des poids
 *  (ou leur décodage, s'il est plus long : GPU à decodeRate). */
function llamaLayerSeconds(gpu: Gpu, bwEfficiency: number): number {
  const b = llamaBench(gpu);
  if (!b) return LLAMA_LAYER_FALLBACK_S;
  const read = Math.max(BENCH_MODEL.bytes / (b.measured.bandwidth * 1e12 * bwEfficiency),
    b.measured.decodeRate ? (BENCH_MODEL.params * 1e9) / b.measured.decodeRate : 0);
  return Math.max(1e-6, (1 / b.tg - read) / BENCH_MODEL.layers);
}
/** llama.cpp, MoE : rendement de la lecture du prompt. Chaque expert ne reçoit qu'une partie des tokens,
 *  d'autant moins qu'ils sont nombreux (gpt-oss-120b, 4 sur 128, plus lent que gpt-oss-20b, 4 sur 32) ;
 *  Metal s'en sort mieux que CUDA, ROCm et Vulkan. */
function llamaMoePrefill(m: ModelPreset, gpu: Gpu): number {
  const split = moeSplit(m);
  if (!split) return 1;
  return (isApple(gpu) ? 0.95 : 0.45) * Math.min(1, 8 * split.share) ** 0.25;
}
const LINK_S = {
  nvlink: { allReduce: 8e-6, hop: 20e-6 },
  pcie: { allReduce: 25e-6, hop: 40e-6 },
  network: { allReduce: 150e-6, hop: 300e-6 },
};
const COMPUTE_EFFICIENCY = 0.5;

export type DecodeSpeed = { perRequest: number; total: number; stepMs: number; bound: "memory" | "compute" };

/** Tokens générés par seconde (par requête et au total) pour une configuration d'inférence. */
export function decodeSpeed(i: Inputs, gpu: Gpu, bwEfficiency: number): DecodeSpeed | null {
  if (i.mode !== "inference") return null;
  const m = i.model;
  const tp = Math.max(1, Math.floor(i.tp)), pp = Math.max(1, Math.floor(i.pp));
  const batch = Math.max(1, Math.floor(i.sequences));
  // À chaque pas, chaque GPU relit les poids actifs (voir activeFor) ; avec un lot de requêtes, la
  // part d'experts routés relus grandit (1 − (1 − k/E)^lot). Octets au prorata des paramètres.
  const bits = engineBits(i);
  const W = i.exactWeightBytes ?? weightBytes(m, bits);
  const Pl = loadedParams(m, isGgufBits(bits)), split = moeSplit(m);
  const shared = Math.max(0, activeFor(m) - (split ? split.experts * split.share : 0));
  const read = split ? shared + split.experts * (1 - (1 - split.share) ** batch) : activeFor(m);
  const weightRead = W * Math.min(1, read / Pl);
  const kvPerGpu = kvBytesPerSequence(m, i.context, i.kvBytes, tp, pp) + (m.stateBytesPerSeq ?? 0) / (tp * pp);
  // Les étages du pipeline s'enchaînent pour un même token : leurs temps s'additionnent.
  // llama.cpp : MoE moins efficace en lecture, coût par couche mesuré pour ce GPU.
  const bw = gpu.bandwidth * 1e12 * bwEfficiency * (i.layerSplit ? llamaBw(m, bits, gpu) : 1);
  const memS = (pp * (weightRead / (tp * pp) + batch * kvPerGpu)) / bw;
  const computeS = (2 * activeFor(m) * 1e9 * batch) / (tp * gpu.tflops * 1e12 * COMPUTE_EFFICIENCY);
  // llama.cpp sur un GPU peu puissant face à sa mémoire (PS5) : décoder les poids lus prend plus de temps
  // que les lire. Les étages du pipeline s'enchaînent, comme pour la mémoire.
  const decodeS = i.layerSplit && gpu.decodeRate ? (read * 1e9) / (tp * gpu.decodeRate) : 0;
  const link = LINK_S[gpu.link];
  // Synchronisation entre GPU du tensoriel : mesurée en NVLink et xGMI (LAYER_US) ; PCIe et réseau, deux
  // all-reduce par couche à la latence du lien.
  const c = LAYER_US[speedFamily(gpu)];
  const syncS = tp <= 1 ? 0 : gpu.link === "nvlink" ? c.tp * Math.log2(tp) * 1e-6 : 2 * link.allReduce;
  const layerS = i.layerSplit ? llamaLayerSeconds(gpu, bwEfficiency) : (m.activeParams ? c.moe : c.dense) * 1e-6;
  const overheadS = m.layers * (layerS + syncS) + (pp - 1) * link.hop;
  const step = Math.max(memS, computeS, decodeS) + overheadS;
  return { perRequest: 1 / step, total: batch / step, stepMs: step * 1000, bound: memS >= Math.max(computeS, decodeS) ? "memory" : "compute" };
}

// ── Temps avant le premier token (prefill) ──────────────────────────────────
// Le prompt est traité d'un bloc : limité par le calcul, pas par la mémoire. 2 × paramètres
// actifs FLOP par token, plus l'attention (QKᵀ et AV) sur les tokens que chaque couche garde,
// avec la largeur du modèle (hidden) comme largeur des requêtes. Tous les GPU travaillent : le
// tensoriel partage chaque calcul, le pipeline traite des morceaux du prompt en parallèle
// (chunked prefill de vLLM, lots de llama.cpp).
// ponytail: rendement de vLLM calé sur un ordre de grandeur public, à recaler sur des mesures :
// sur H100 en BF16, Llama 8B lit environ 20 000 tokens de prompt par seconde. llama.cpp : le débit
// mesuré de chaque GPU (llamaBench).
const PREFILL_EFFICIENCY = 0.35;

/** Tokens qu'un token voit en moyenne dans un groupe de couches, pour un prompt de `tokens`. */
function attendedTokens(g: KvGroup, tokens: number) {
  if (g.window) return Math.min(tokens / 2, g.window);
  if (g.ratio) return tokens / (2 * g.ratio);
  return tokens / 2;
}

/** Secondes pour traiter un prompt de `tokens` (une requête) sur `gpus` cartes, avant le premier token
 *  généré. llamacpp : couches réparties entre les cartes, qui se passent les lots de 512 tokens du prompt
 *  à la chaîne (4 lots par appel au plus : à partir de 2K tokens, 2 cartes vont 1,6 fois plus vite
 *  qu'une, 8 cartes 2,9 fois ; un prompt de 512 tokens, un seul lot, n'en profite pas). */
export function prefillSeconds(model: ModelPreset, tokens: number, gpu: Gpu, gpus = 1, llamacpp = false, pp = 1): number {
  const T = Math.max(1, tokens), n = Math.max(1, gpus);
  const dense = 2 * activeFor(model) * 1e9 * T;
  const attention = model.kv.reduce((sum, g) => sum + g.layers * 4 * model.hidden * T * attendedTokens(g, T), 0);
  const bench = llamacpp ? llamaBench(gpu) : null;
  if (!bench) {
    // vLLM, SGLang : les étages du pipeline ne travaillent en même temps que sur des lots différents du prompt
    // (max_num_batched_tokens de vLLM : 8 192 sur un GPU de 70 Gio ou plus, 2 048 sinon). Un prompt court, un seul
    // lot, traverse les étages l'un après l'autre : 48 RTX 4090 en 8 × 6 et un prompt de 1K, 27 ms annoncés avant
    // (tous les GPU comptés), six fois trop peu.
    const stages = Math.max(1, Math.min(Math.floor(pp), n));
    const lots = Math.ceil(T / ((gpu.memGiB ?? gpu.vram) >= 70 ? 8192 : 2048));
    const parallel = (n / stages) * ((stages * lots) / (lots + stages - 1));
    return (dense + attention) / (parallel * gpu.tflops * 1e12 * PREFILL_EFFICIENCY);
  }
  const lots = Math.min(LLAMACPP_UBATCHES, Math.ceil(T / LLAMACPP_UBATCH_TOKENS));
  const pipeline = (n * lots) / (lots + n - 1);
  return (dense + LLAMACPP_ATTENTION * attention) / (pipeline * bench.pp * benchFlops * llamaMoePrefill(model, gpu));
}

// ── Déchargement vers la RAM (llama.cpp, Ollama, LM Studio) ─────────────────
// Un modèle trop gros pour la VRAM tourne quand même si une partie des poids reste en RAM :
//  - MoE : les experts de certaines couches en RAM (--n-cpu-moe N). Le GPU garde l'attention,
//    les experts partagés et le cache KV ; chaque token ne relit en RAM que ses experts actifs.
//  - Dense : des couches entières en RAM (-ngl = couches gardées sur le GPU), cache KV compris.
// Vitesse : chaque token relit sa part des poids dans les deux mémoires, l'une après l'autre.
// Estimation (±30 à 50 %) : la RAM ne délivre qu'une part de sa bande passante théorique.

/** Bande passante théorique de la RAM (Go/s) : canaux × débit (MT/s) × 8 octets. */
export const RAM_TYPES = [
  { id: "ddr4-2", label: "DDR4-3200, 2 channels (most desktops)", bandwidth: 51.2 },
  { id: "ddr5-2", label: "DDR5-5600, 2 channels (recent desktops, laptops)", bandwidth: 89.6 },
  { id: "ddr5-4", label: "DDR5-5600, 4 channels (Threadripper)", bandwidth: 179.2 },
  { id: "ddr5-8", label: "DDR5-5600, 8 channels (Threadripper PRO, Xeon W)", bandwidth: 358.4 },
  { id: "ddr5-12", label: "DDR5-4800, 12 channels (EPYC 9004)", bandwidth: 460.8 },
] as const;
const RAM_EFFICIENCY = 0.55;
/** Par couche calculée sur le processeur : échange des activations avec le GPU et synchronisation. */
const CPU_LAYER_S = 150e-6;
/** Laissé au système et aux autres programmes. */
export const RAM_RESERVED_GIB = 6;
/**
 * Part des experts routés lue par token (k parmi E), que les presets ne donnent pas : sert à
 * séparer les poids des experts du reste. Environ la moitié des paramètres actifs vient des
 * experts routés, sans dépasser 1/16 (8 sur 128). Contrôlé sur des configs publiées : DeepSeek V3
 * (8 sur 256) donne 19 B hors experts pour 17 B réels, gpt-oss-120b (4 sur 128) 2,6 B pour ~2,4 B.
 */
const routedShare = (m: ModelPreset) => Math.min(1 / 16, (m.activeParams ?? m.params) / (2 * m.params));

/**
 * MoE : milliards de paramètres des experts routés et part lue par token. Exacts quand le preset a
 * son recensement et son routage (k experts sur E) ; sinon estimés depuis les paramètres actifs.
 */
export function moeSplit(m: ModelPreset): { experts: number; share: number } | null {
  if (!m.activeParams) return null;
  const P = loadedParams(m);
  if (m.parts?.experts && m.routing) return { experts: Math.min(P, m.parts.experts), share: m.routing.active / m.routing.experts };
  if (m.routing) {
    // Routage connu (config.json), pas le recensement : experts déduits des paramètres actifs.
    const share = m.routing.active / m.routing.experts, inputEmbed = m.tiedEmbeddings ? 0 : vocabParams(m) / 1e9;
    return { experts: Math.min(P, Math.max(0, (P - m.activeParams - inputEmbed) / (1 - share))), share };
  }
  const experts = Math.min(P, (P - m.activeParams) / (1 - routedShare(m)));
  return { experts, share: Math.max(0, (m.activeParams - (P - experts)) / experts) };
}

/**
 * Milliards de paramètres relus à chaque token : le modèle chargé, sans l'encodeur de vision (il ne
 * sert qu'aux images), l'embedding d'entrée et les tables de correspondance (une ligne par token),
 * ni les experts routés non choisis. C'est la définition des « paramètres actifs » des fiches : le
 * recensement redonne gpt-oss-120b 5,13 (annoncé 5,1), DeepSeek R1 36,6 (37), Qwen3.6 35B-A3B 2,94 (3).
 * MoE sans recensement (modèle importé) : le chiffre annoncé.
 */
export function activeFor(m: ModelPreset): number {
  const split = moeSplit(m);
  if (m.activeParams && !(m.parts?.experts && m.routing)) return m.activeParams;
  const inputEmbed = m.tiedEmbeddings ? 0 : vocabParams(m) / 1e9;
  return loadedParams(m) - (m.parts?.vision ?? 0) - (m.parts?.lookup ?? 0) - inputEmbed - (split ? split.experts * (1 - split.share) : 0);
}

export type Offload = {
  kind: "experts" | "layers";
  /** Couches dont les experts (MoE) ou la totalité (dense) restent en RAM. */
  cpuLayers: number;
  layers: number;
  vramGiB: number;
  ramGiB: number;
  /** Tokens générés par seconde, une requête. */
  tokensPerSecond: number;
  /** Option llama.cpp correspondante. */
  flag: string;
};

/**
 * Plan de déchargement pour `gpus` cartes et la RAM donnée. null : le modèle tient déjà en VRAM,
 * ou il ne tient pas non plus avec la RAM (ramShort : GiB qui manquent alors).
 */
export function offloadPlan(i: Inputs, gpu: Gpu, gpus: number, ram: { gib: number; bandwidth: number }, bwEfficiency: number):
  { plan: Offload | null; ramShort?: number } {
  if (i.mode !== "inference") return { plan: null };
  const m = i.model, L = m.layers;
  const reserve = reserveFor(i, 1, gpus); // llama.cpp : couches réparties, un GPU par étage
  const vram = gpus * (Math.max(0, i.vramGiB) * reserve.utilization - reserve.overheadGiB) * GiB;
  // L'embedding d'entrée et les tables lues ligne par ligne restent en RAM (llama.cpp) ; une tête de
  // sortie partagée est une copie de l'embedding sur le GPU.
  const bits = engineBits({ ...i, layerSplit: true });
  const total = i.exactWeightBytes ?? weightBytes(m, bits);
  const inRamAlways = embeddingsInRam({ ...i, layerSplit: true });
  const W = total - (m.tiedEmbeddings ? lookupBytes(m, bits, total) : inRamAlways);
  const lookup = m.parts?.lookup ?? 0;
  const seqs = Math.max(1, i.sequences);
  const kv = kvBytesPerSequence(m, i.context, i.kvBytes) * seqs + (m.stateBytesPerSeq ?? 0) * seqs;
  if (W + kv <= vram) return { plan: null };
  const ramRoom = Math.max(0, ram.gib - RAM_RESERVED_GIB) * GiB - inRamAlways;
  const split = moeSplit(m);
  // Mêmes rendements que decodeSpeed avec llama.cpp : sans couche en RAM, la même vitesse.
  const gpuBw = gpu.bandwidth * 1e12 * bwEfficiency * llamaBw(m, bits, gpu), ramBw = ram.bandwidth * 1e9 * RAM_EFFICIENCY;
  const perLayerS = L * llamaLayerSeconds(gpu, bwEfficiency);

  if (split) {
    // Poids des experts routés et du reste (attention, experts partagés, embeddings).
    const expertBytes = W * Math.min(1, split.experts / (loadedParams(m, isGgufBits(bits)) - lookup)), sharedBytes = W - expertBytes;
    const activeShare = split.share; // part des experts lue par token
    const perLayer = expertBytes / L;
    if (sharedBytes + kv <= vram) {
      const gpuLayers = Math.min(L, Math.floor((vram - sharedBytes - kv) / perLayer));
      const cpuLayers = L - gpuLayers, inRam = cpuLayers * perLayer;
      if (inRam > ramRoom) return { plan: null, ramShort: (inRam - ramRoom) / GiB };
      const s = (sharedBytes + gpuLayers * perLayer * activeShare + kvBytesPerSequence(m, i.context, i.kvBytes)) / gpuBw
        + (cpuLayers * perLayer * activeShare) / ramBw + perLayerS + cpuLayers * CPU_LAYER_S;
      return {
        plan: {
          kind: "experts", cpuLayers, layers: L, vramGiB: (sharedBytes + gpuLayers * perLayer + kv) / GiB, ramGiB: (inRam + inRamAlways) / GiB,
          tokensPerSecond: 1 / s, flag: `--n-cpu-moe ${cpuLayers}`,
        },
      };
    }
  }
  // Couches entières : poids et cache KV de chaque couche restent ensemble.
  const perLayer = (W + kv) / L;
  const gpuLayers = Math.max(0, Math.min(L, Math.floor(vram / perLayer)));
  const cpuLayers = L - gpuLayers, inRam = cpuLayers * perLayer;
  if (inRam > ramRoom) return { plan: null, ramShort: (inRam - ramRoom) / GiB };
  const a = activeFor(m) / (loadedParams(m) - lookup);
  const readPerLayer = (W * a + kvBytesPerSequence(m, i.context, i.kvBytes)) / L;
  const s = (gpuLayers * readPerLayer) / gpuBw + (cpuLayers * readPerLayer) / ramBw + perLayerS + cpuLayers * CPU_LAYER_S;
  return {
    plan: {
      kind: "layers", cpuLayers, layers: L, vramGiB: (gpuLayers * perLayer) / GiB, ramGiB: (inRam + inRamAlways) / GiB,
      tokensPerSecond: 1 / s, flag: `-ngl ${gpuLayers}`,
    },
  };
}

/** Prix des API par modèle (OpenRouter), $ par million de tokens, clé = id Hugging Face en minuscules. */
export type ApiPrices = Record<string, { id: string; name: string; input: number; output: number }>;

/** L'offre d'API retenue pour un modèle : fournisseur et prix en $ par million de tokens ; offers : nombre
 *  d'offres comparées. */
export type ApiOffer = { provider: string; input: number; output: number; via: "openrouter" | "huggingface"; offers: number };

/**
 * Le seul prix d'API que montre le calculateur (métrique, seuil de rentabilité, rapport) : l'offre la moins
 * chère en sortie parmi le prix public d'OpenRouter (fichier de prix) et les fournisseurs d'Inference
 * Providers de Hugging Face (catalogue). Deux sources affichées côte à côte se contredisaient (Llama 3.3 70B,
 * 14 octobre 2026 : 0,79 $ chez OpenRouter, « dès 0,40 $ » chez Novita juste en dessous). À prix égal, le
 * fournisseur nommé passe avant OpenRouter, qui revend.
 */
export function cheapestApi(
  openrouter: { input: number; output: number } | undefined,
  providers: readonly { name: string; input: number; output: number }[] = [],
): ApiOffer | null {
  const all = [
    ...providers.map((p) => ({ provider: p.name, input: p.input, output: p.output, via: "huggingface" as const })),
    ...(openrouter ? [{ provider: "OpenRouter", input: openrouter.input, output: openrouter.output, via: "openrouter" as const }] : []),
  ].filter((o) => o.output > 0 && o.input >= 0);
  const best = [...all].sort((a, b) => a.output - b.output || a.input - b.input)[0];
  return best ? { ...best, offers: all.length } : null;
}

/** Prix OpenRouter d'un dépôt : sa clé exacte, sinon le même modèle sous un autre nom ou dans une autre précision.
 *  OpenRouter garde parfois l'ancien nom du dépôt (« meta-llama/Meta-Llama-3.1-8B-Instruct », renommé
 *  « Llama-3.1-8B-Instruct » sur Hugging Face) ou sert une autre précision (Nemotron 3 Super : BF16 ici, FP8
 *  chez OpenRouter). Sans cela, « No API offer » pour Llama 3.1 8B (14 octobre 2026). */
export function openRouterPrice<T>(apis: Readonly<Record<string, T>> | undefined, repo: string | undefined): T | undefined {
  if (!apis || !repo) return undefined;
  const key = repo.toLowerCase();
  if (apis[key]) return apis[key];
  const hit = Object.keys(apis).find((k) => repoKey(k) === repoKey(key));
  return hit ? apis[hit] : undefined;
}
/** Nom d'un dépôt sans casse, sans l'ancien préfixe « Meta- » de Llama 3.1 ni suffixe de précision : deux noms
 *  du même modèle donnent la même clé (aussi pour la fiche modèle, scripts/sources/modelcard.ts). */
export const repoKey = (k: string) => k.toLowerCase().replace(/\/meta-llama-/, "/llama-").replace(/-(bf16|fp8|nvfp4|fp4|mxfp4)$/, "");

/**
 * Coût auto-hébergé face à une API. utilizationToBreakEven : part du temps où les GPU
 * doivent générer pour égaler l'API (au-delà de 1, l'API reste moins chère même à pleine charge).
 */
export function selfHostVsApi(hourlyUsd: number, tokensPerSecond: number, apiOutputPer1M: number) {
  const selfPer1M = (hourlyUsd / (tokensPerSecond * 3600)) * 1e6;
  const utilizationToBreakEven = selfPer1M / apiOutputPer1M;
  return { selfPer1M, utilizationToBreakEven, breakEvenTokensPerDay: utilizationToBreakEven * tokensPerSecond * 86400 };
}
